import { PREVIEW_REFERENCE } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

interface AttachmentPreviewProps {
  logoUrl: string | null
  footer: string
}

// Abstract placeholder rows — the article scope is not org-specific here.
const ROWS = [0, 1, 2]

/** Preview of the PDF questionnaire attachment — reflects the logo and PDF footer. */
export function AttachmentPreview({ logoUrl, footer }: AttachmentPreviewProps) {
  const { translate } = useTranslation()
  return (
    <div className="rounded-lg bg-white px-6 py-5 text-[12.5px] leading-relaxed text-[#222] shadow-lg">
      <div className="flex items-start justify-between gap-4 border-b border-[#ececec] pb-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={translate("MC_TPL_LOGO_ALT")}
            className="h-6 max-w-[160px] object-contain"
          />
        ) : (
          <span className="h-6 w-24 rounded bg-[#ededed]" aria-hidden />
        )}
        <div className="font-mono text-right text-[10.5px] leading-[1.7] text-[#888]">
          <div>
            {translate("MC_TPL_PREVIEW_REF_PREFIX", { ref: PREVIEW_REFERENCE })}
          </div>
        </div>
      </div>

      <div className="py-4">
        <div className="text-[15px] font-semibold text-[#111]">
          {translate("MC_TPL_PREVIEW_DOC_TITLE")}
        </div>
        <div className="mt-0.5 text-[12px] text-[#666]">
          {translate("MC_TPL_PREVIEW_DOC_SUBTITLE", {
            name: translate("MC_TPL_PREVIEW_SUPPLIER_NAME"),
          })}
        </div>
      </div>

      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr className="bg-[#efefe9] text-left text-[#222]">
            <th className="border border-[#c9c9c4] px-2 py-1.5">
              {translate("MC_TPL_PREVIEW_COL_ARTICLE_NO")}
            </th>
            <th className="border border-[#c9c9c4] px-2 py-1.5">
              {translate("MC_TPL_PREVIEW_COL_DESCRIPTION")}
            </th>
            <th className="border border-[#c9c9c4] px-2 py-1.5">
              {translate("MC_TPL_PREVIEW_COL_SVHC")}
            </th>
            <th className="border border-[#c9c9c4] px-2 py-1.5">
              {translate("MC_TPL_PREVIEW_COL_CONC")}
            </th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row}>
              <td className="border border-[#d9d9d4] px-2 py-1.5">
                <span className="block h-2 w-16 rounded bg-[#ededed]" />
              </td>
              <td className="border border-[#d9d9d4] px-2 py-1.5">
                <span className="block h-2 w-24 rounded bg-[#ededed]" />
              </td>
              <td className="border border-[#d9d9d4] bg-yellow1/12 px-2 py-1.5" />
              <td className="border border-[#d9d9d4] bg-yellow1/12 px-2 py-1.5" />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-5 border-t border-[#ececec] pt-3 text-[10.5px] leading-[1.6] whitespace-pre-line text-[#999]">
        {footer}
      </div>
    </div>
  )
}
